import { Link } from "@tanstack/react-router";
import { DiscordLogo, InstagramLogo, TiktokLogo, XLogo, YoutubeLogo } from "@phosphor-icons/react";
import { LogoHorizontal } from "@/components/brand/LogoHorizontal";

const PRODUCT_LINKS = [
  { label: "Marketplace", to: "/marketplace" },
  { label: "Communities", to: "/communities" },
  { label: "Creators", to: "/explore" },
  { label: "Jobs", to: "/jobs" },
  { label: "Explore", to: "/explore" },
] as const;

const COMPANY_LINKS = ["About", "Careers", "Blog", "Press", "Brand"] as const;
const RESOURCE_LINKS = ["Help Center", "Guidelines", "Community", "Developer Docs", "Contact"] as const;
const LEGAL_LINKS = ["Terms of Service", "Privacy Policy", "Cookie Policy"] as const;

const SOCIAL_LINKS = [
  { label: "Discord", href: "https://discord.gg/studside", icon: DiscordLogo },
  { label: "X", href: "https://x.com/studside", icon: XLogo },
  { label: "YouTube", href: "https://youtube.com/@studside", icon: YoutubeLogo },
  { label: "Instagram", href: "https://instagram.com/studside", icon: InstagramLogo },
  { label: "TikTok", href: "https://tiktok.com/@studside", icon: TiktokLogo },
] as const;

const linkClassName = "text-small text-text-secondary transition-colors hover:text-text-primary";
const columnTitleClassName = "text-small font-medium text-text-primary";
const socialLinkClassName =
  "inline-flex size-10 items-center justify-center rounded-sm text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-2 focus-visible:outline-primary";

/**
 * Decorative echo of the logomark's modular blocks — two bare outlines and one
 * glowing primary block, joined by a hairline. Purely ornamental (aria-hidden);
 * the real lockup lives in the brand column below and is never itself altered
 * (DESIGN.md §2.4 forbids recoloring/effects on the approved mark).
 */
function FooterGraphic() {
  return (
    <svg viewBox="0 0 132 116" className="hidden h-[116px] w-[132px] shrink-0 lg:block" aria-hidden="true">
      <defs>
        <filter id="footer-graphic-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
      </defs>
      <line
        x1="86"
        y1="40"
        x2="62"
        y2="64"
        className="stroke-border-strong"
        strokeWidth="1.5"
        strokeDasharray="2 5"
        strokeLinecap="round"
      />
      <rect x="64" y="2" width="42" height="42" rx="12" className="fill-none stroke-border-strong" strokeWidth="1.5" />
      <rect x="2" y="46" width="54" height="54" rx="14" className="fill-none stroke-border-strong" strokeWidth="1.5" />
      <rect x="50" y="56" width="50" height="50" rx="14" className="fill-primary" opacity="0.55" filter="url(#footer-graphic-glow)" />
      <rect x="50" y="56" width="50" height="50" rx="14" className="fill-primary" />
    </svg>
  );
}

/** Marketing footer — DESIGN.md §8.2 container + nav destinations; layout/copy from studside-footer-concept1.png. */
export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-surface-1">
      <div className="mx-auto max-w-(--breakpoint-xl) px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-8">
            <div className="max-w-72">
              <p className="text-h3 whitespace-nowrap text-text-primary">
                <span className="block">More creators.</span>
                <span className="block">A brighter tomorrow.</span>
              </p>
              <div className="mt-4 h-px w-8 bg-primary" aria-hidden="true" />
            </div>

            <FooterGraphic />

            <div className="max-w-72">
              <Link to="/" aria-label="Studside home" className="inline-flex">
                <LogoHorizontal />
              </Link>
              <p className="mt-4 text-small text-text-secondary">
                The creator district for Roblox. Build. Share. Sell. Connect.
              </p>
              <ul className="mt-6 flex items-center gap-1">
                {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
                  <li key={label}>
                    <a href={href} target="_blank" rel="noreferrer" aria-label={label} className={socialLinkClassName}>
                      <Icon size={20} weight="regular" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 sm:gap-x-12">
            <nav aria-label="Product">
              <h3 className={columnTitleClassName}>Product</h3>
              <ul className="mt-4 space-y-3">
                {PRODUCT_LINKS.map((link) => (
                  <li key={link.label}>
                    <Link to={link.to} className={linkClassName}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Company">
              <h3 className={columnTitleClassName}>Company</h3>
              <ul className="mt-4 space-y-3">
                {COMPANY_LINKS.map((label) => (
                  <li key={label}>
                    <a href="#" className={linkClassName}>
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Resources">
              <h3 className={columnTitleClassName}>Resources</h3>
              <ul className="mt-4 space-y-3">
                {RESOURCE_LINKS.map((label) => (
                  <li key={label}>
                    <a href="#" className={linkClassName}>
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-border-subtle pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-small text-text-muted">
            © {year} Studside. Works with Roblox — not affiliated with Roblox Corporation.
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {LEGAL_LINKS.map((label) => (
              <li key={label}>
                <a href="#" className="text-small text-text-muted transition-colors hover:text-text-secondary">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
